import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import T from '../i18n/content.js';
import { useLang, useT } from '../i18n/LangContext.jsx';
import MediaCarousel from './MediaCarousel.jsx';

export default function InsightsFeed({
  notes, notesPage, totalNotesPages, notesPending, notesError,
  onNotesPageChange, onNotesPagePrefetch, onNotesRetry,
  media, mediaPending, mediaError, onMediaRetry,
}) {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState('notes');
  const [mediaIndex, setMediaIndex] = useState(0);

  const S = T.insightsSec;
  const featured = media[mediaIndex];

  // The Long-form & Media tab is shown only when the backend actually has media
  // for the current language; with none it stays hidden, leaving just Notes.
  // Uploading media (admin) makes the tab reappear on its own — no code change.
  const hasMedia = media.length > 0;
  const TABS = hasMedia ? ['notes', 'media'] : ['notes'];

  return (
    <section className="section feed">
      {/* 两个 tab 共用同一外壳；不再因 media 切换而改形（保持左块尺寸/网格/宽度一致） */}
      <div className="wrap feed__wrap">
        <aside className="feed__title-block">
          {lang === 'en' ? (
            <>
              <div className="feed__t2">News<br />and<br />Insights</div>
              <div className="feed__t3">
                Information is abundant.<br />Judgment is not.
              </div>
            </>
          ) : (
            <>
              <div className="feed__t2 cn">资 讯<br />与<br />洞 见</div>
              <div className="feed__t3 cn">
                信 息 从 不 稀 缺<br />判 断 方 为 难 得
              </div>
            </>
          )}
        </aside>

        <span className="feed__rule" aria-hidden="true" />

        <div className="feed__content">
          <div className="feed__tabs" role="tablist">
            {TABS.map((key) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === key}
                className={`feed__tab ${tab === key ? 'is-active' : ''}`}
                onClick={() => setTab(key)}
              >
                {t(S.tabs[key])}
              </button>
            ))}
          </div>

          {mediaError && (
            <div className="feed__request-error">
              <p role="alert">{t(S.mediaError)}</p>
              <button type="button" className="feed__retry" disabled={mediaPending} onClick={onMediaRetry}>
                {t(S.retry)}
              </button>
            </div>
          )}

          {tab === 'notes' && (
            <>
              <div className="feed__notes" aria-busy={notesPending}>
                {notes.map((n) => (
                  <a className="note-row"
                     href={`/insights/${n.id}`}
                     key={n.id}
                     onClick={e => { e.preventDefault(); navigate(`/insights/${n.id}`); }}>
                    <div className="note-row__meta">
                      {/* series/category (n.cat) temporarily hidden on the frontend */}
                      <span>{n.date}</span>
                    </div>
                    <div className="note-row__title">{n.title}</div>
                    <div className="note-row__excerpt">{n.excerpt}</div>
                  </a>
                ))}
                {notes.length === 0 && (
                  <p className="feed__media-empty">{lang === 'en' ? 'No articles yet.' : '暂 无 文 章。'}</p>
                )}
              </div>

              {notesError && (
                <div className="feed__request-error">
                  <p role="alert">{t(S.articlesError)}</p>
                  <button type="button" className="feed__retry" disabled={notesPending} onClick={onNotesRetry}>
                    {t(S.retry)}
                  </button>
                </div>
              )}

              {totalNotesPages > 1 && (
                <nav className="feed__pager" aria-label={lang === 'en' ? 'Notes pagination' : '短 札 分 页'}>
                  <button
                    type="button"
                    className="pager__nav"
                    disabled={notesPending || notesPage === 1}
                    onClick={() => onNotesPageChange(notesPage - 1)}
                    onPointerEnter={() => { if (notesPage > 1) onNotesPagePrefetch(notesPage - 1); }}
                    onFocus={() => { if (notesPage > 1) onNotesPagePrefetch(notesPage - 1); }}
                    onTouchStart={() => { if (notesPage > 1) onNotesPagePrefetch(notesPage - 1); }}
                  >
                    {lang === 'en' ? 'Previous' : '上 一 页'}
                  </button>
                  <ol className="pager__pages">
                    {Array.from({ length: totalNotesPages }, (_, i) => i + 1).map((n) => (
                      <li key={n}>
                        <button
                          type="button"
                          className={`pager__num ${n === notesPage ? 'is-active' : ''}`}
                          aria-current={n === notesPage ? 'page' : undefined}
                          disabled={notesPending || n === notesPage}
                          onClick={() => onNotesPageChange(n)}
                          onPointerEnter={() => onNotesPagePrefetch(n)}
                          onFocus={() => onNotesPagePrefetch(n)}
                          onTouchStart={() => onNotesPagePrefetch(n)}
                        >
                          {n}
                        </button>
                      </li>
                    ))}
                  </ol>
                  <button
                    type="button"
                    className="pager__nav"
                    disabled={notesPending || notesPage === totalNotesPages}
                    onClick={() => onNotesPageChange(notesPage + 1)}
                    onPointerEnter={() => { if (notesPage < totalNotesPages) onNotesPagePrefetch(notesPage + 1); }}
                    onFocus={() => { if (notesPage < totalNotesPages) onNotesPagePrefetch(notesPage + 1); }}
                    onTouchStart={() => { if (notesPage < totalNotesPages) onNotesPagePrefetch(notesPage + 1); }}
                  >
                    {lang === 'en' ? 'Next' : '下 一 页'}
                  </button>
                </nav>
              )}
            </>
          )}

          {tab === 'media' && (
            <div className="feed__media">
              {media.length === 0 ? (
                <p className="feed__media-empty">{t(S.mediaEmpty)}</p>
              ) : (
                <>
                  <header className="feed__media-head">
                    <h3 className="feed__media-title">{featured.title}</h3>
                    <div className="feed__media-date">{featured.date}</div>
                    <p className="feed__media-intro">{featured.intro}</p>
                  </header>
                  <MediaCarousel items={media} onSlideChange={setMediaIndex} />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
